/*
 * useVoiceRecorder — record audio (NOT live dictation), then transcribe.
 *
 * Flow: start() → MediaRecorder captures a clip while a Web-Audio AnalyserNode
 * feeds the live waveform → stop() ends the clip → it's POSTed to Whisper
 * (/api/intake/transcribe) → the transcript is handed back via onTranscript so
 * the composer can drop it into the input for the user to review and send.
 *
 * Status: idle → recording → transcribing → idle (or error).
 * The analyser node is exposed (ref) so <VoiceWaveform> can draw real levels.
 */
import { useCallback, useRef, useState } from 'react';

import { transcribeAudio } from '../lib/intakeApi';

export type RecorderStatus = 'idle' | 'recording' | 'transcribing' | 'error';

function pickMimeType(): string {
  const prefs = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  const MR = typeof MediaRecorder !== 'undefined' ? MediaRecorder : null;
  if (!MR || !MR.isTypeSupported) return '';
  for (const t of prefs) {
    try { if (MR.isTypeSupported(t)) return t; } catch { /* ignore */ }
  }
  return '';
}

export interface VoiceRecorder {
  status: RecorderStatus;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
}

export function useVoiceRecorder(onTranscript: (text: string) => void): VoiceRecorder {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const canceledRef = useRef(false);

  const teardownAudio = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { void audioCtxRef.current?.close(); } catch { /* ignore */ }
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    canceledRef.current = false;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording isn’t supported in this browser.');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2500);
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone access was blocked.');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2500);
      return;
    }
    streamRef.current = stream;

    // Analyser feeds the waveform — optional, never blocks recording.
    try {
      const Ctx: typeof AudioContext =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const srcNode = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.75;
      srcNode.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
    } catch { /* waveform is cosmetic — keep recording */ }

    const mime = pickMimeType();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      teardownAudio();
      if (canceledRef.current) { setStatus('idle'); return; }
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      if (blob.size < 1200) { setStatus('idle'); return; }  // too short — ignore
      setStatus('transcribing');
      try {
        const text = await transcribeAudio(blob);
        if (text && text.trim()) onTranscript(text.trim());
        setStatus('idle');
      } catch {
        setError('Couldn’t transcribe that — try again.');
        setStatus('error');
        setTimeout(() => setStatus('idle'), 2500);
      }
    };
    recorderRef.current = rec;
    rec.start();
    setStatus('recording');
  }, [teardownAudio, onTranscript]);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  const cancel = useCallback(() => {
    canceledRef.current = true;
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    } else {
      teardownAudio();
      setStatus('idle');
    }
  }, [teardownAudio]);

  return { status, error, start, stop, cancel, analyserRef };
}
